import React, { useState, useEffect, useRef } from "react";
import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import {
  MessageCircle,
  X,
  Send,
  Minimize2,
  Bot,
  User,
  Loader2,
  ChevronDown,
  Image as ImageIcon,
  Paperclip,
  Bug,
  Box,
  Building2,
  Globe,
  Calculator,
  Users,
  Inbox,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

// Map product.icon names from the registry to actual lucide components.
// Falls back to Box for any value we haven't whitelisted.
const PRODUCT_ICON_MAP = {
  Building2,
  Globe,
  Calculator,
  Users,
  Inbox,
  Box,
};

// Map product.color names (Tailwind palette family) to a small set of styles
// the widget can apply. Keep this in sync with SupportChatsPage / DevBacklog.
const PRODUCT_COLOR_CLASSES = {
  orange: "bg-orange-500/15 text-orange-300 border-orange-500/30",
  amber: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  yellow: "bg-yellow-500/15 text-yellow-300 border-yellow-500/30",
  blue: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  emerald: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  violet: "bg-violet-500/15 text-violet-300 border-violet-500/30",
  zinc: "bg-zinc-700/40 text-zinc-300 border-zinc-600/40",
};

const productClass = (color) => PRODUCT_COLOR_CLASSES[color] || PRODUCT_COLOR_CLASSES.zinc;

const SupportChat = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [pendingImage, setPendingImage] = useState(null);
  const [isBugReport, setIsBugReport] = useState(false);
  const [products, setProducts] = useState([]);
  const [selectedProductSlug, setSelectedProductSlug] = useState(null);
  const [selectedInstanceSlug, setSelectedInstanceSlug] = useState(null);
  const [selectedMirrorSlug, setSelectedMirrorSlug] = useState(null);
  const [showProductPicker, setShowProductPicker] = useState(false);
  // Three-step picker: product → instance (multi-tenant) → mirror (multi-mirror).
  // Each step is skipped automatically when there's nothing to pick (HUB has
  // no instances; instances with 0–1 mirrors skip the mirror step).
  const [pickerStage, setPickerStage] = useState("product"); // "product" | "instance" | "mirror"
  const [pendingProductSlug, setPendingProductSlug] = useState(null);
  const [pendingInstanceSlug, setPendingInstanceSlug] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  // Load user's previous sessions and product registry on mount
  useEffect(() => {
    if (user && isOpen) {
      loadMySessions();
      loadProducts();
    }
  }, [user, isOpen]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const loadMySessions = async () => {
    try {
      const response = await api.get("/support-chat/my-sessions");
      setSessions(response.data || []);
    } catch (error) {
      console.error("Error loading sessions:", error);
    }
  };

  const loadProducts = async () => {
    try {
      const response = await api.get("/support-products", { params: { active_only: true } });
      const list = response.data || [];
      setProducts(list);
      // Default selection: previously chosen by user (per-browser), or
      // role-specific default (e.g. Vexos for partners), or the global
      // is_default, else first item.
      const remembered = typeof localStorage !== "undefined" ? localStorage.getItem("so_support_product") : null;
      const fromRemembered = remembered && list.find((p) => p.slug === remembered);
      const isPartner = (user?.role || "").toLowerCase() === "partner";
      const fromPartnerDefault = isPartner ? list.find((p) => p.partner_default) : null;
      const fromDefault = list.find((p) => p.is_default);
      const initial = fromRemembered || fromPartnerDefault || fromDefault || list[0] || null;
      setSelectedProductSlug(initial?.slug || null);

      // Per-deployment instance + mirror auto-pick. KEY DECISION: for any role
      // for whom the product mandates instance/mirror picks
      // (`require_instance_for_roles` / `require_mirror_for_roles`), we DO NOT
      // pre-fill from the default or from localStorage. Reasons:
      //   1. Pre-filling defeats the whole "force the user to think about
      //      which deployment they're reporting" gate — they'd just send
      //      against the default they never actively chose.
      //   2. localStorage persistence is fine for non-required products
      //      (admin's HUB chat: same context every time) but dangerous for
      //      partners who chat across many Vexos deployments per week —
      //      they'd land on whichever they picked last and likely send to
      //      the wrong one.
      // For non-gated cases keep the existing remembered → default → first
      // cascade so the chat opens ready-to-send.
      const requiresInstancePick = isPartner
        && Array.isArray(initial?.require_instance_for_roles)
        && initial.require_instance_for_roles.includes("partner");
      const requiresMirrorPick = isPartner
        && Array.isArray(initial?.require_mirror_for_roles)
        && initial.require_mirror_for_roles.includes("partner");

      const instances = initial?.instances || [];
      let initialInst = null;
      if (!requiresInstancePick) {
        const rememberedInst = typeof localStorage !== "undefined"
          ? localStorage.getItem("so_support_product_instance")
          : null;
        const fromRememberedInst = rememberedInst && instances.find((i) => i.slug === rememberedInst && i.is_active !== false);
        const fromDefaultInst = instances.find((i) => i.is_default && i.is_active !== false);
        const fallbackInst = instances.find((i) => i.is_active !== false);
        initialInst = fromRememberedInst || fromDefaultInst || fallbackInst || null;
      }
      setSelectedInstanceSlug(initialInst?.slug || null);

      let initialMirrorSlug = null;
      if (initialInst && !requiresMirrorPick) {
        const rememberedMir = typeof localStorage !== "undefined"
          ? localStorage.getItem("so_support_product_mirror")
          : null;
        const mirrors = (initialInst.mirrors || []).filter((m) => m.is_active !== false);
        const fromRememberedMir = rememberedMir && mirrors.find((m) => m.slug === rememberedMir);
        initialMirrorSlug = fromRememberedMir?.slug || null;
      }
      setSelectedMirrorSlug(initialMirrorSlug);

      // For required-pick roles, open the picker straight away on the right
      // step so the partner sees "Which deployment is this about?" instead
      // of a chat input that silently swallows messages until the backend
      // 400s. Single-product partners go straight to instance step.
      if (requiresInstancePick && initial && instances.filter((i) => i.is_active !== false).length > 0) {
        setPendingProductSlug(initial.slug);
        setPendingInstanceSlug(null);
        setPickerStage("instance");
        setShowProductPicker(true);
      }
    } catch (error) {
      console.error("Error loading products:", error);
    }
  };

  const loadSession = async (sid) => {
    try {
      const response = await api.get(`/support-chat/sessions/${sid}`);
      setSessionId(sid);
      setMessages(response.data.messages || []);
      setShowHistory(false);
      // Stay on the session's original product/instance/mirror so the AI keeps context.
      if (response.data.product_slug) {
        setSelectedProductSlug(response.data.product_slug);
      }
      if (response.data.product_instance_slug) {
        setSelectedInstanceSlug(response.data.product_instance_slug);
      } else if (response.data.product_slug) {
        // Reset instance if session was on a single-tenant product.
        setSelectedInstanceSlug(null);
      }
      if (response.data.product_instance_mirror_slug) {
        setSelectedMirrorSlug(response.data.product_instance_mirror_slug);
      } else {
        setSelectedMirrorSlug(null);
      }
    } catch (error) {
      console.error("Error loading session:", error);
    }
  };

  const persistSelection = (productSlug, instanceSlug, mirrorSlug) => {
    if (typeof localStorage === "undefined") return;
    if (productSlug) localStorage.setItem("so_support_product", productSlug);
    if (instanceSlug) {
      localStorage.setItem("so_support_product_instance", instanceSlug);
    } else {
      localStorage.removeItem("so_support_product_instance");
    }
    if (mirrorSlug) {
      localStorage.setItem("so_support_product_mirror", mirrorSlug);
    } else {
      localStorage.removeItem("so_support_product_mirror");
    }
  };

  const closePicker = () => {
    setShowProductPicker(false);
    setPickerStage("product");
    setPendingProductSlug(null);
    setPendingInstanceSlug(null);
  };

  // Step 1: user picks a product. If it has live instances we step into
  // instance pick (Step 2). Otherwise commit immediately.
  const pickProduct = (slug) => {
    const product = products.find((p) => p.slug === slug);
    const liveInstances = (product?.instances || []).filter((i) => i.is_active !== false);
    if (liveInstances.length > 0) {
      setPendingProductSlug(slug);
      setPendingInstanceSlug(null);
      setPickerStage("instance");
      return;
    }
    // Single-tenant: commit + close.
    setSelectedProductSlug(slug);
    setSelectedInstanceSlug(null);
    setSelectedMirrorSlug(null);
    persistSelection(slug, null, null);
    closePicker();
  };

  // Step 2: user picks an instance (or "Platform-wide" → slug=null). If the
  // chosen instance has 2+ live mirrors we step into mirror pick. Otherwise
  // commit immediately.
  const pickInstance = (instanceSlug) => {
    const productSlug = pendingProductSlug || selectedProductSlug;
    if (!productSlug) return;
    if (instanceSlug) {
      const product = products.find((p) => p.slug === productSlug);
      const inst = (product?.instances || []).find((i) => i.slug === instanceSlug);
      const liveMirrors = (inst?.mirrors || []).filter((m) => m.is_active !== false);
      // Any active mirror means at least 2 URLs total (primary + mirror) —
      // step into mirror pick so the user disambiguates production vs
      // test/staging. Picker UI synthesizes a "primary" card from
      // instance.url at the top of the mirrors list.
      if (liveMirrors.length >= 1) {
        setPendingInstanceSlug(instanceSlug);
        setPickerStage("mirror");
        return;
      }
      // 0 mirrors: only one URL exists (instance.url), commit instance and
      // close. Backend prompt already inlines this URL.
      setSelectedProductSlug(productSlug);
      setSelectedInstanceSlug(instanceSlug);
      setSelectedMirrorSlug(null);
      persistSelection(productSlug, instanceSlug, null);
      closePicker();
      return;
    }
    // "Platform-wide / not sure" — keep product, drop instance + mirror.
    setSelectedProductSlug(productSlug);
    setSelectedInstanceSlug(null);
    setSelectedMirrorSlug(null);
    persistSelection(productSlug, null, null);
    closePicker();
  };

  // Step 3: user picks a mirror (or null = "any mirror" of this deployment).
  const pickMirror = (mirrorSlug) => {
    const productSlug = pendingProductSlug || selectedProductSlug;
    const instanceSlug = pendingInstanceSlug || selectedInstanceSlug;
    if (!productSlug || !instanceSlug) return;
    setSelectedProductSlug(productSlug);
    setSelectedInstanceSlug(instanceSlug);
    setSelectedMirrorSlug(mirrorSlug || null);
    persistSelection(productSlug, instanceSlug, mirrorSlug || null);
    closePicker();
  };

  const openPicker = (stage = "product", productSlug = null, instanceSlug = null) => {
    setShowProductPicker(true);
    if (stage === "mirror" && productSlug && instanceSlug) {
      setPendingProductSlug(productSlug);
      setPendingInstanceSlug(instanceSlug);
      setPickerStage("mirror");
    } else if (stage === "instance" && productSlug) {
      setPendingProductSlug(productSlug);
      setPendingInstanceSlug(null);
      setPickerStage("instance");
    } else {
      setPickerStage("product");
      setPendingProductSlug(null);
      setPendingInstanceSlug(null);
    }
  };

  const startNewChat = () => {
    setSessionId(null);
    setMessages([]);
    setShowHistory(false);
    setPendingImage(null);
    setIsBugReport(false);
    // Force-pick at the start of a brand-new chat so the user explicitly
    // confirms which product (and which deployment + mirror) they're
    // reporting about. EXCEPTION: when only one product is available
    // (e.g. partners only see Vexos), skip the product step entirely and
    // open the instance picker for that one product — showing a single
    // card titled "Which product?" is just noise.
    const initialProduct = products.length === 1 ? products[0] : null;
    if (initialProduct) {
      const liveInstances = (initialProduct.instances || []).filter((i) => i.is_active !== false);
      setSelectedProductSlug(initialProduct.slug);
      if (liveInstances.length > 0) {
        setPendingProductSlug(initialProduct.slug);
        setPendingInstanceSlug(null);
        setPickerStage("instance");
        setShowProductPicker(true);
      } else {
        // Single-tenant single-product — no picker needed at all.
        setShowProductPicker(false);
        setPickerStage("product");
        setPendingProductSlug(null);
        setPendingInstanceSlug(null);
      }
      return;
    }
    setPickerStage("product");
    setPendingProductSlug(null);
    setPendingInstanceSlug(null);
    setShowProductPicker(true);
  };

  const startBugReport = () => {
    setSessionId(null);
    setMessages([]);
    setShowHistory(false);
    setPendingImage(null);
    setIsBugReport(true);
    // Soft, conversational opener — for partners especially we want a chat,
    // not a numbered list to fill in. The AI follows up with one question
    // at a time (per partner_intent_guidance in the backend).
    const isPartner = ((user?.role || "").toLowerCase()) === "partner";
    const opener = isPartner
      ? "🐛 Tell me what's wrong — in your own words.\n\nWhat were you trying to do, and what happened instead? A screenshot helps too (paste it or use 📎)."
      : "🐛 Bug Report Mode\n\nIn a sentence or two: what were you doing, and what broke? Attach a screenshot if it helps (📎 or paste).";
    setMessages([{
      role: "assistant",
      content: opener,
      timestamp: new Date().toISOString()
    }]);
  };

  // Shared image-upload core, used by both the file input button and the
  // clipboard paste handler. Returns true on success.
  const uploadImageFile = async (file) => {
    if (!file) return false;
    if (!file.type.startsWith('image/')) {
      alert('Please select an image');
      return false;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('File too large. Maximum 5MB');
      return false;
    }
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('/support-chat/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPendingImage(response.data.image_url);
      return true;
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image');
      return false;
    } finally {
      setUploadingImage(false);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    await uploadImageFile(file);
    // Reset file input so the same file can be reselected after removal.
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Paste a screenshot directly into the message input (Cmd/Ctrl+V).
  // Browsers expose pasted images via clipboardData.items as files of type
  // image/png. We grab the first image item, prevent default (so a giant
  // base64 string doesn't land in the textbox), and route through the
  // same uploader as the paperclip button.
  const handlePaste = async (event) => {
    const items = event.clipboardData?.items || [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        event.preventDefault();
        const file = item.getAsFile();
        if (file) {
          await uploadImageFile(file);
        }
        return;
      }
    }
    // No image — let the default text paste happen.
  };

  const removePendingImage = () => {
    setPendingImage(null);
  };

  // Resolve the selected product / instance / mirror up front so the
  // partner-gating logic below can reference them without hitting TDZ on
  // re-render once a partner user is loaded. (The original layout declared
  // these consts further down, after the partner gating — minifier preserved
  // source order, and once user.role === "partner" the && short-circuit
  // stopped firing on `selectedProduct`, throwing ReferenceError.)
  const selectedProduct = products.find((p) => p.slug === selectedProductSlug) || null;
  const selectedInstance = (selectedProduct?.instances || []).find(
    (i) => i.slug === selectedInstanceSlug && i.is_active !== false
  ) || null;
  const selectedMirror = (selectedInstance?.mirrors || []).find(
    (m) => m.slug === selectedMirrorSlug && m.is_active !== false
  ) || null;

  // For partners, certain products require deployment+mirror picks before
  // any chat is allowed. We compute this from product.require_instance/mirror_for_roles.
  const isPartner = ((user?.role || "").toLowerCase()) === "partner";
  const partnerRequiresInstance = isPartner
    && selectedProduct
    && Array.isArray(selectedProduct.require_instance_for_roles)
    && selectedProduct.require_instance_for_roles.includes("partner")
    && (selectedProduct.instances || []).filter((i) => i.is_active !== false).length > 0;
  const partnerRequiresMirror = isPartner
    && selectedInstance
    && Array.isArray(selectedProduct?.require_mirror_for_roles)
    && selectedProduct.require_mirror_for_roles.includes("partner")
    // Trigger as soon as there's any mirror — the partner picks between the
    // primary URL (instance.url) and the mirror(s). 0 mirrors = nothing to
    // disambiguate.
    && (selectedInstance.mirrors || []).filter((m) => m.is_active !== false).length >= 1;
  const partnerMissingInstance = partnerRequiresInstance && !selectedInstanceSlug;
  const partnerMissingMirror = partnerRequiresMirror && !selectedMirrorSlug;
  const sendBlockedReason = partnerMissingInstance
    ? `Pick which ${selectedProduct?.short_label || selectedProduct?.name} deployment first`
    : partnerMissingMirror
      ? "Pick which URL (production / staging / …) first"
      : null;

  const sendMessage = async () => {
    if ((!inputValue.trim() && !pendingImage) || isLoading) return;
    if (sendBlockedReason) {
      // Open the picker on whichever step is missing instead of letting them
      // hit the backend's 400. Better UX than a toast.
      if (partnerMissingInstance) {
        openPicker("instance", selectedProduct.slug);
      } else if (partnerMissingMirror) {
        openPicker("mirror", selectedProduct.slug, selectedInstance.slug);
      }
      return;
    }

    // Send the user's text as-is. The image goes via the `image_url` field
    // (backend handles AI awareness through the system prompt — no need to
    // inject "[Attached image: …]" markers into the message text, which used
    // to leak into the rendered chat history. Even with empty text, allow
    // sending — backend gets image_url and the screenshot itself is the
    // message.)
    const messageContent = inputValue.trim() || (pendingImage ? "(screenshot attached)" : "");

    const currentPage = typeof window !== "undefined"
      ? window.location.pathname + window.location.search
      : null;

    const userMessage = {
      role: "user",
      content: messageContent,
      image_url: pendingImage,
      current_page: currentPage,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue("");
    const imageToSend = pendingImage;
    const wasBugReport = isBugReport;
    setPendingImage(null);
    setIsLoading(true);

    try {
      const response = await api.post("/support-chat/send", {
        message: messageContent,
        session_id: sessionId,
        image_url: imageToSend,
        is_bug_report: wasBugReport,
        current_page: currentPage,
        product_slug: selectedProductSlug || undefined,
        product_instance_slug: selectedInstanceSlug || undefined,
        product_instance_mirror_slug: selectedMirrorSlug || undefined,
      });

      if (!sessionId) {
        setSessionId(response.data.session_id);
      }

      const assistantMessage = {
        role: "assistant",
        content: response.data.response,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error("Error sending message:", error);
      // Backend returns 400 with structured detail when a partner missed
      // a required selection (deployment / mirror). Show that as a calm
      // assistant message instead of a generic "something went wrong".
      let detail = error?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.error && detail.message) {
        const errorMessage = {
          role: "assistant",
          content: `⚠ ${detail.message}`,
          timestamp: new Date().toISOString(),
        };
        setMessages(prev => [...prev, errorMessage]);
        // Auto-open the right picker step so they can fix it without scrolling.
        if (detail.error === "deployment_required" && selectedProduct) {
          openPicker("instance", selectedProduct.slug);
        } else if (detail.error === "mirror_required" && selectedProduct && selectedInstance) {
          openPicker("mirror", selectedProduct.slug, selectedInstance.slug);
        }
      } else {
        const errorMessage = {
          role: "assistant",
          content: "Sorry, something went wrong. Please try again later.",
          timestamp: new Date().toISOString(),
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Don't show for non-authenticated users (selectedProduct/instance/mirror
  // are computed earlier so the partner-gating block above has them in scope).
  if (!user) return null;

  const ProductIcon = selectedProduct
    ? PRODUCT_ICON_MAP[selectedProduct.icon] || Box
    : Box;
  // Picker derived: for step 2 we show the pending (or currently-selected)
  // product's instances; for step 3 we show the pending instance's mirrors.
  const pickerProductForInstances = (pickerStage === "instance" || pickerStage === "mirror")
    ? products.find((p) => p.slug === (pendingProductSlug || selectedProductSlug))
    : selectedProduct;
  const pickerInstances = (pickerProductForInstances?.instances || []).filter(
    (i) => i.is_active !== false
  );
  const pickerInstanceForMirrors = pickerStage === "mirror"
    ? (pickerProductForInstances?.instances || []).find(
        (i) => i.slug === (pendingInstanceSlug || selectedInstanceSlug)
      )
    : null;
  const pickerMirrors = (pickerInstanceForMirrors?.mirrors || []).filter(
    (m) => m.is_active !== false
  );

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className={cn(
            "fixed bottom-20 right-6 z-50",
            "w-14 h-14 rounded-full",
            "bg-gradient-to-r from-orange-500 to-orange-600",
            "shadow-lg shadow-orange-500/25",
            "flex items-center justify-center",
            "hover:scale-110 transition-transform duration-200",
            "group"
          )}
          data-testid="support-chat-button"
        >
          <MessageCircle className="w-6 h-6 text-zinc-50" />
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-zinc-950" />
        </button>
      )}

      {/* Chat Window — sized for actual reading: ~480px wide × min(740px, 80vh).
          Partners describing bugs paste long stack traces / multi-paragraph
          context, and earlier 384×512 was forcing them to scroll constantly.
          Caps at viewport height/width so it works on small laptops too. */}
      {isOpen && (
        <div
          className={cn(
            "fixed bottom-20 right-6 z-50",
            "bg-zinc-900 rounded-2xl border border-zinc-800",
            "w-[30rem] max-w-[calc(100vw-3rem)]",
            "shadow-2xl shadow-black/50",
            "flex flex-col overflow-hidden",
            "transition-all duration-300",
            isMinimized ? "h-14" : "h-[min(46rem,80vh)]"
          )}
          data-testid="support-chat-window"
        >
          {/* Header */}
          <div 
            className={cn(
              "flex items-center justify-between px-4 py-3",
              "bg-gradient-to-r from-orange-500/20 to-orange-600/20",
              "border-b border-zinc-800 cursor-pointer"
            )}
            onClick={() => setIsMinimized(!isMinimized)}
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0">
                <Bot className="w-4 h-4 text-zinc-50" />
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-zinc-50 truncate">AI Support</h3>
                {selectedProduct ? (
                  <div className="mt-0.5 flex items-center gap-1 flex-wrap">
                    {/* When the user has access to only one product (e.g. partners
                        only see Vexos), the chip is informational — no chevron,
                        no click handler. With 2+ products it's a picker toggle. */}
                    {products.length > 1 ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (showProductPicker) {
                            closePicker();
                          } else {
                            openPicker("product");
                          }
                        }}
                        className={cn(
                          "inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-medium truncate",
                          productClass(selectedProduct.color)
                        )}
                        title="Change product"
                      >
                        <ProductIcon className="w-3 h-3" />
                        <span className="truncate">{selectedProduct.short_label || selectedProduct.name}</span>
                        <ChevronDown className="w-3 h-3 opacity-70" />
                      </button>
                    ) : (
                      <span
                        className={cn(
                          "inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-medium truncate",
                          productClass(selectedProduct.color)
                        )}
                      >
                        <ProductIcon className="w-3 h-3" />
                        <span className="truncate">{selectedProduct.short_label || selectedProduct.name}</span>
                      </span>
                    )}
                    {(selectedProduct.instances || []).filter((i) => i.is_active !== false).length > 0 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openPicker("instance", selectedProduct.slug);
                        }}
                        className={cn(
                          "inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-medium truncate",
                          selectedInstance
                            ? "border-zinc-600 bg-zinc-800/80 text-zinc-200"
                            : "border-amber-500/40 bg-amber-500/10 text-amber-300"
                        )}
                        title={selectedInstance ? "Change deployment" : "Pick a deployment"}
                      >
                        <span className="truncate">
                          {selectedInstance ? selectedInstance.label : "any deployment"}
                        </span>
                        <ChevronDown className="w-3 h-3 opacity-70" />
                      </button>
                    )}
                    {selectedInstance && (selectedInstance.mirrors || []).filter((m) => m.is_active !== false).length >= 2 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openPicker("mirror", selectedProduct.slug, selectedInstance.slug);
                        }}
                        className={cn(
                          "inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-medium truncate",
                          selectedMirror
                            ? "border-zinc-600 bg-zinc-800/60 text-zinc-300"
                            : "border-amber-500/30 bg-amber-500/5 text-amber-300/80"
                        )}
                        title={selectedMirror ? "Change mirror" : "Pick a mirror"}
                      >
                        <span className="truncate">
                          {selectedMirror ? selectedMirror.label : "any mirror"}
                        </span>
                        <ChevronDown className="w-3 h-3 opacity-70" />
                      </button>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-zinc-400 truncate">Ask about any of our products</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-zinc-400 hover:text-zinc-50"
                onClick={(e) => { e.stopPropagation(); setIsMinimized(!isMinimized); }}
              >
                {isMinimized ? <ChevronDown className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-zinc-400 hover:text-zinc-50"
                onClick={(e) => { e.stopPropagation(); setIsOpen(false); }}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Content (hidden when minimized) */}
          {!isMinimized && (
            <>
              {/* Action buttons */}
              <div className="flex items-center gap-2 px-4 py-2 border-b border-zinc-800">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs border-zinc-700"
                  onClick={startNewChat}
                >
                  New Chat
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className={cn(
                    "h-7 text-xs border-red-500/50 text-red-400 hover:bg-red-500/10",
                    isBugReport && "bg-red-500/20"
                  )}
                  onClick={startBugReport}
                >
                  <Bug className="w-3 h-3 mr-1" />
                  Report Bug
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className={cn(
                    "h-7 text-xs border-zinc-700",
                    showHistory && "bg-zinc-800"
                  )}
                  onClick={() => { setShowHistory(!showHistory); loadMySessions(); }}
                >
                  History ({sessions.length})
                </Button>
              </div>

              {/* Product / instance picker — shown on New Chat or when the
                  user clicks a chip in the header. Two-step:
                    1. pick the product (HUB / Spinhouse / Vexos)
                    2. for multi-tenant products, pick the specific deployment
                  Both selections are sent on each message and persisted on
                  the session so AI replies + KB scope stay relevant. */}
              {showProductPicker && products.length > 0 && pickerStage === "product" && (
                <div className="px-3 py-3 border-b border-zinc-800 bg-zinc-900/80">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[11px] uppercase tracking-wide text-zinc-400">
                      Which product is this about?
                    </div>
                    <button
                      onClick={closePicker}
                      className="text-zinc-500 hover:text-zinc-200"
                      title="Close"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-1.5">
                    {products.map((p) => {
                      const Icon = PRODUCT_ICON_MAP[p.icon] || Box;
                      const active = p.slug === selectedProductSlug;
                      const liveCount = (p.instances || []).filter((i) => i.is_active !== false).length;
                      return (
                        <button
                          key={p.slug}
                          onClick={() => pickProduct(p.slug)}
                          className={cn(
                            "flex items-center gap-2 px-2 py-1.5 rounded border text-left transition-colors",
                            active
                              ? `${productClass(p.color)} ring-1 ring-orange-400/40`
                              : "border-zinc-700 bg-zinc-900/40 text-zinc-300 hover:bg-zinc-800/60"
                          )}
                          title={p.description || p.name}
                        >
                          <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                          <span className="text-xs font-medium truncate flex-1">
                            {p.short_label || p.name}
                          </span>
                          {liveCount > 0 && (
                            <span className="text-[9px] text-zinc-500 ml-auto flex-shrink-0">
                              {liveCount}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Step 2: instance picker for multi-tenant products. */}
              {showProductPicker && pickerStage === "instance" && pickerProductForInstances && (
                <div className="px-3 py-3 border-b border-zinc-800 bg-zinc-900/80">
                  <div className="flex items-center justify-between mb-2 gap-2">
                    <button
                      onClick={() => { setPickerStage("product"); setPendingProductSlug(null); setPendingInstanceSlug(null); }}
                      className="text-[11px] text-zinc-500 hover:text-zinc-200 flex items-center gap-1"
                      title="Back to products"
                    >
                      <ChevronDown className="w-3 h-3 rotate-90" /> back
                    </button>
                    <div className="text-[11px] uppercase tracking-wide text-zinc-400 flex-1 text-center">
                      Which {pickerProductForInstances.short_label || pickerProductForInstances.name}?
                    </div>
                    <button
                      onClick={closePicker}
                      className="text-zinc-500 hover:text-zinc-200"
                      title="Close"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="space-y-1 max-h-56 overflow-y-auto">
                    {pickerInstances.map((inst) => {
                      const active = inst.slug === selectedInstanceSlug
                        && (pendingProductSlug || selectedProductSlug) === pickerProductForInstances.slug;
                      const liveMirrors = (inst.mirrors || []).filter((m) => m.is_active !== false).length;
                      return (
                        <button
                          key={inst.slug}
                          onClick={() => pickInstance(inst.slug)}
                          className={cn(
                            "w-full flex items-center gap-2 px-2 py-1.5 rounded border text-left transition-colors",
                            active
                              ? "border-orange-500/40 bg-orange-500/10 text-orange-200 ring-1 ring-orange-400/30"
                              : "border-zinc-700 bg-zinc-900/40 text-zinc-300 hover:bg-zinc-800/60"
                          )}
                          title={inst.url || inst.label}
                        >
                          <span className="text-xs font-medium truncate flex-1">{inst.label}</span>
                          {inst.is_default && (
                            <span className="text-[9px] text-orange-400">★</span>
                          )}
                          {liveMirrors >= 2 && (
                            <span className="text-[9px] text-zinc-500" title={`${liveMirrors} mirrors`}>
                              {liveMirrors}m
                            </span>
                          )}
                          {inst.url && (
                            <span className="text-[9px] text-zinc-500 truncate font-mono">
                              {inst.url.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                            </span>
                          )}
                        </button>
                      );
                    })}
                    {/* Hide "Platform-wide / not sure" escape for roles
                        that the product marks as required (partners on
                        Vexos must pick a deployment — period). */}
                    {!(isPartner
                       && Array.isArray(pickerProductForInstances.require_instance_for_roles)
                       && pickerProductForInstances.require_instance_for_roles.includes("partner")
                      ) && (
                      <button
                        onClick={() => pickInstance(null)}
                        className={cn(
                          "w-full flex items-center gap-2 px-2 py-1.5 rounded border text-left transition-colors mt-2",
                          !selectedInstanceSlug && (pendingProductSlug || selectedProductSlug) === pickerProductForInstances.slug
                            ? "border-amber-500/40 bg-amber-500/10 text-amber-200"
                            : "border-zinc-700 bg-zinc-900/40 text-zinc-400 hover:bg-zinc-800/60"
                        )}
                        title="Use this when the question isn't about a specific deployment"
                      >
                        <span className="text-xs font-medium truncate flex-1">
                          Platform-wide / not sure
                        </span>
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Step 3: mirror picker — shown when the chosen deployment
                  has 1+ active mirrors. The instance's primary URL is
                  rendered as a synthesized "Production" card at the top so
                  the partner has both options visible: primary vs each
                  alternate URL (staging, alt-domain, …). Picking the primary
                  resolves to mirror_slug=null on the session. */}
              {showProductPicker && pickerStage === "mirror" && pickerInstanceForMirrors && (
                <div className="px-3 py-3 border-b border-zinc-800 bg-zinc-900/80">
                  <div className="flex items-center justify-between mb-2 gap-2">
                    <button
                      onClick={() => { setPickerStage("instance"); setPendingInstanceSlug(null); }}
                      className="text-[11px] text-zinc-500 hover:text-zinc-200 flex items-center gap-1"
                      title="Back to deployments"
                    >
                      <ChevronDown className="w-3 h-3 rotate-90" /> back
                    </button>
                    <div className="text-[11px] uppercase tracking-wide text-zinc-400 flex-1 text-center truncate">
                      Which URL of {pickerInstanceForMirrors.label}?
                    </div>
                    <button
                      onClick={closePicker}
                      className="text-zinc-500 hover:text-zinc-200"
                      title="Close"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="space-y-1 max-h-56 overflow-y-auto">
                    {/* Synthesized "primary" card from instance.url — picking
                        it resolves to mirror_slug=null. Appears only when the
                        instance has a primary URL configured. */}
                    {pickerInstanceForMirrors.url && (
                      <button
                        onClick={() => pickMirror(null)}
                        className={cn(
                          "w-full flex items-center gap-2 px-2 py-1.5 rounded border text-left transition-colors",
                          !selectedMirrorSlug && (pendingInstanceSlug || selectedInstanceSlug) === pickerInstanceForMirrors.slug
                            ? "border-orange-500/40 bg-orange-500/10 text-orange-200 ring-1 ring-orange-400/30"
                            : "border-zinc-700 bg-zinc-900/40 text-zinc-300 hover:bg-zinc-800/60"
                        )}
                        title={pickerInstanceForMirrors.url}
                      >
                        <span className="text-xs font-medium truncate flex-1">
                          Production <span className="text-[9px] text-zinc-500 font-normal">(primary)</span>
                        </span>
                        <span className="text-[9px] text-zinc-500 truncate font-mono">
                          {pickerInstanceForMirrors.url.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                        </span>
                      </button>
                    )}
                    {pickerMirrors.map((mir) => {
                      const active = mir.slug === selectedMirrorSlug
                        && (pendingInstanceSlug || selectedInstanceSlug) === pickerInstanceForMirrors.slug;
                      return (
                        <button
                          key={mir.slug}
                          onClick={() => pickMirror(mir.slug)}
                          className={cn(
                            "w-full flex items-center gap-2 px-2 py-1.5 rounded border text-left transition-colors",
                            active
                              ? "border-orange-500/40 bg-orange-500/10 text-orange-200 ring-1 ring-orange-400/30"
                              : "border-zinc-700 bg-zinc-900/40 text-zinc-300 hover:bg-zinc-800/60"
                          )}
                          title={mir.url || mir.label}
                        >
                          <span className="text-xs font-medium truncate flex-1">{mir.label}</span>
                          {mir.url && (
                            <span className="text-[9px] text-zinc-500 truncate font-mono">
                              {mir.url.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                            </span>
                          )}
                        </button>
                      );
                    })}
                    {/* Same "no escape" for partners on products that mark
                        mirror as required. */}
                    {!(isPartner
                       && Array.isArray(pickerProductForInstances?.require_mirror_for_roles)
                       && pickerProductForInstances.require_mirror_for_roles.includes("partner")
                      ) && (
                      <button
                        onClick={() => pickMirror(null)}
                        className={cn(
                          "w-full flex items-center gap-2 px-2 py-1.5 rounded border text-left transition-colors mt-2",
                          !selectedMirrorSlug && (pendingInstanceSlug || selectedInstanceSlug) === pickerInstanceForMirrors.slug
                            ? "border-amber-500/40 bg-amber-500/10 text-amber-200"
                            : "border-zinc-700 bg-zinc-900/40 text-zinc-400 hover:bg-zinc-800/60"
                        )}
                        title="Skip mirror picking — AI will ask if needed"
                      >
                        <span className="text-xs font-medium truncate flex-1">
                          Any / not sure
                        </span>
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Bug report indicator */}
              {isBugReport && (
                <div className="px-4 py-2 bg-red-500/10 border-b border-red-500/20 flex items-center gap-2">
                  <Bug className="w-4 h-4 text-red-400" />
                  <span className="text-xs text-red-300">Bug Report Mode - Describe the issue</span>
                  <button 
                    onClick={() => setIsBugReport(false)}
                    className="ml-auto text-red-400 hover:text-red-300"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}

              {/* History panel */}
              {showHistory && (
                <div className="flex-1 overflow-y-auto p-2 space-y-1 bg-zinc-800/50">
                  {sessions.length === 0 ? (
                    <p className="text-sm text-zinc-500 text-center py-4">No previous chats</p>
                  ) : (
                    sessions.map((s) => {
                      const sProduct = products.find((p) => p.slug === s.product_slug);
                      return (
                        <div
                          key={s.id}
                          className={cn(
                            "p-2 rounded-lg cursor-pointer hover:bg-zinc-700/50",
                            sessionId === s.id && "bg-zinc-700/50"
                          )}
                          onClick={() => loadSession(s.id)}
                        >
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <Badge
                              variant="outline"
                              className={cn(
                                "text-[10px]",
                                s.category === "bug" ? "border-red-500/50 text-red-400" :
                                s.category === "feature" ? "border-blue-500/50 text-blue-400" :
                                s.category === "complaint" ? "border-yellow-500/50 text-yellow-400" :
                                "border-zinc-600 text-zinc-400"
                              )}
                            >
                              {s.category || "question"}
                            </Badge>
                            {sProduct && (
                              <span className={cn(
                                "inline-flex items-center px-1.5 py-0.5 rounded border text-[10px]",
                                productClass(sProduct.color)
                              )}>
                                {sProduct.short_label || sProduct.name}
                              </span>
                            )}
                            {s.product_instance_label && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded border border-zinc-600 bg-zinc-800/60 text-[10px] text-zinc-300">
                                {s.product_instance_label}
                              </span>
                            )}
                            {s.product_instance_mirror_label && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded border border-zinc-700 bg-zinc-800/40 text-[10px] text-zinc-400">
                                {s.product_instance_mirror_label}
                              </span>
                            )}
                            <span className="ml-auto text-[10px] text-zinc-500">
                              {new Date(s.updated_at).toLocaleDateString()}
                            </span>
                          </div>
                          <p className="text-xs text-zinc-300 mt-1 truncate">
                            {s.messages?.[0]?.content?.substring(0, 50) || "..."}
                          </p>
                        </div>
                      );
                    })
                  )}
                </div>
              )}

              {/* Messages */}
              {!showHistory && (
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {messages.length === 0 && (
                    <div className="text-center py-8">
                      <Bot className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                      <p className="text-sm text-zinc-400">
                        Hi! I'm the portal AI assistant.
                      </p>
                      <p className="text-xs text-zinc-500 mt-1">
                        Ask questions, report bugs, or suggest ideas.
                      </p>
                    </div>
                  )}

                  {messages.map((msg, idx) => (
                    <div
                      key={idx}
                      className={cn(
                        "flex gap-2",
                        msg.role === "user" ? "justify-end" : "justify-start"
                      )}
                    >
                      {msg.role !== "user" && (
                        <div className={cn(
                          "w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0",
                          msg.role === "admin" ? "bg-purple-500" : "bg-orange-500"
                        )}>
                          <Bot className="w-3 h-3 text-zinc-50" />
                        </div>
                      )}
                      <div
                        className={cn(
                          "max-w-[80%] px-3 py-2 rounded-2xl text-sm",
                          msg.role === "user"
                            ? "bg-orange-500 text-white rounded-br-md"
                            : msg.role === "admin"
                            ? "bg-purple-500/20 text-purple-100 rounded-bl-md border border-purple-500/30"
                            : "bg-zinc-800 text-zinc-200 rounded-bl-md"
                        )}
                      >
                        {msg.role === "admin" && (
                          <p className="text-[10px] text-purple-400 mb-1">
                            {msg.admin_name || "Admin"}
                          </p>
                        )}
                        {/* Image rendering with legacy fallback. Old saved
                            sessions had the URL only inside the
                            "[Attached image: /uploads/...]" marker (image_url
                            field was dropped on save before PR #28). Extract
                            it from the marker so old screenshots still
                            render. New messages have image_url directly. */}
                        {(() => {
                          const raw = msg.content || "";
                          const markerMatch = raw.match(/\[Attached image:\s*([^\]\s]+)\s*\]/);
                          const cleaned = raw.replace(/\n?\[Attached image:[^\]]*\]/g, "").trim();
                          const imgSrc = msg.image_url || (markerMatch ? markerMatch[1] : null);
                          return (
                            <>
                              {imgSrc && (
                                <a
                                  href={imgSrc}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="block mb-2"
                                >
                                  <img
                                    src={imgSrc}
                                    alt="Attached"
                                    className="max-w-full rounded-lg border border-white/20"
                                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                                  />
                                </a>
                              )}
                              {cleaned ? <p className="whitespace-pre-wrap">{cleaned}</p> : null}
                            </>
                          );
                        })()}
                        {msg.role === "user" && msg.current_page && (
                          <p className="text-[10px] text-zinc-50/70 mt-1 font-mono">
                            on {msg.current_page}
                          </p>
                        )}
                      </div>
                      {msg.role === "user" && (
                        <div className="w-6 h-6 rounded-full bg-zinc-700 flex items-center justify-center flex-shrink-0">
                          <User className="w-3 h-3 text-zinc-300" />
                        </div>
                      )}
                    </div>
                  ))}

                  {isLoading && (
                    <div className="flex gap-2">
                      <div className="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center">
                        <Bot className="w-3 h-3 text-zinc-50" />
                      </div>
                      <div className="bg-zinc-800 px-4 py-2 rounded-2xl rounded-bl-md">
                        <Loader2 className="w-4 h-4 text-zinc-400 animate-spin" />
                      </div>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>
              )}

              {/* Input */}
              {!showHistory && (
                <div className="p-3 border-t border-zinc-800">
                  {/* Pending image preview */}
                  {pendingImage && (
                    <div className="mb-2 relative inline-block">
                      <img 
                        src={pendingImage}
                        alt="Attached image"
                        className="h-16 rounded-lg border border-zinc-700"
                      />
                      <button
                        onClick={removePendingImage}
                        className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center"
                      >
                        <X className="w-3 h-3 text-zinc-50" />
                      </button>
                    </div>
                  )}
                  {/* Partner-mode: show what's missing before they can send.
                      Click jumps directly to the right picker step. */}
                  {sendBlockedReason && (
                    <button
                      type="button"
                      onClick={() => {
                        if (partnerMissingInstance) {
                          openPicker("instance", selectedProduct.slug);
                        } else if (partnerMissingMirror) {
                          openPicker("mirror", selectedProduct.slug, selectedInstance.slug);
                        }
                      }}
                      className="w-full text-left px-2.5 py-1.5 mb-2 rounded border border-amber-500/40 bg-amber-500/10 text-[11px] text-amber-200 hover:bg-amber-500/20"
                    >
                      ⚠ {sendBlockedReason} — click to pick.
                    </button>
                  )}
                  <div className="flex gap-2">
                    {/* Hidden file input */}
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleImageUpload}
                      accept="image/*"
                      className="hidden"
                    />
                    {/* Attach button */}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isLoading || uploadingImage}
                      className="text-zinc-400 hover:text-zinc-50"
                      title="Attach screenshot"
                    >
                      {uploadingImage ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Paperclip className="w-4 h-4" />
                      )}
                    </Button>
                    <Input
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyPress={handleKeyPress}
                      onPaste={handlePaste}
                      placeholder={
                        sendBlockedReason
                          ? sendBlockedReason
                          : pendingImage
                            ? "Add description..."
                            : "Type a message or paste a screenshot..."
                      }
                      className="flex-1 bg-zinc-800 border-zinc-700 text-zinc-50 placeholder:text-zinc-500"
                      disabled={isLoading || !!sendBlockedReason}
                      data-testid="support-chat-input"
                    />
                    <Button
                      onClick={sendMessage}
                      disabled={(!inputValue.trim() && !pendingImage) || isLoading || !!sendBlockedReason}
                      className="bg-orange-500 hover:bg-orange-600"
                      size="icon"
                      data-testid="support-chat-send"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
};

export default SupportChat;
